/**
 * Mobile-only bottom tab bar for app-like navigation.
 * Layout: [Home] [Library] [+] [Achievements] [Statistics]
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Award, BarChart2, CreditCard, Folder, Home, Layers, Plus } from "lucide-react";
import { AppState } from "@/types/linguadeck";
import type { DeckInitialType } from "./DeckModal";

const TABS_LEFT: { id: AppState["currentView"]; path: string; icon: typeof Home; labelKey: string }[] = [
  { id: "home", path: "/", icon: Home, labelKey: "sidebar.home" },
  { id: "library", path: "/library", icon: Layers, labelKey: "sidebar.mySets" },
];

const TABS_RIGHT: { id: AppState["currentView"]; path: string; icon: typeof Home; labelKey: string }[] = [
  { id: "gamification", path: "/gamification", icon: Award, labelKey: "header.achievements" },
  { id: "dashboard", path: "/dashboard", icon: BarChart2, labelKey: "statistics.pageTitle" },
];

interface BottomTabBarProps {
  currentView: AppState["currentView"];
  onNavTo: (view: AppState["currentView"]) => void;
  onNewDeck?: (type: DeckInitialType) => void;
  onCreateFolder?: () => void;
}

export default function BottomTabBar({
  currentView,
  onNavTo,
  onNewDeck,
  onCreateFolder,
}: BottomTabBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [fabOpen, setFabOpen] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const canCreate = !!onNewDeck && !!onCreateFolder;

  useEffect(() => {
    if (!fabOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!fabRef.current?.contains(event.target as Node)) {
        setFabOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [fabOpen]);

  const handleTab = (view: AppState["currentView"], path: string) => {
    setFabOpen(false);
    onNavTo(view);
    navigate(path);
  };

  const isActive = (tab: (typeof TABS_LEFT)[0] | (typeof TABS_RIGHT)[0]) =>
    location.pathname === tab.path ||
    (tab.path !== "/" && tab.path.length > 1 && location.pathname.startsWith(tab.path));

  const homeTab = TABS_LEFT[0];
  const libraryTab = TABS_LEFT[1];
  const achievementsTab = TABS_RIGHT[0];
  const statsTab = TABS_RIGHT[1];

  const handleCreateDeck = () => {
    if (!onNewDeck) return;
    setFabOpen(false);
    onNewDeck("cards");
  };

  const handleCreateFolder = () => {
    if (!onCreateFolder) return;
    setFabOpen(false);
    onCreateFolder();
  };

  const TabButton = ({
    tab,
  }: {
    tab: (typeof TABS_LEFT)[0] | (typeof TABS_RIGHT)[0];
  }) => {
    const Icon = tab.icon;
    const active = isActive(tab);
    return (
      <button
        type="button"
        onClick={() => handleTab(tab.id, tab.path)}
        className="flex flex-col items-center justify-center gap-0.5 py-2 px-3 transition-colors"
        style={{
          color: active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontFamily: "DM Sans, sans-serif",
        }}
        aria-current={active ? "page" : undefined}
        aria-label={t(tab.labelKey)}
      >
        <Icon size={22} strokeWidth={active ? 2.5 : 2} className="shrink-0" />
        <span className="text-[10px] font-medium truncate max-w-full">{t(tab.labelKey)}</span>
      </button>
    );
  };

  return (
    <nav
      className="fixed bottom-0 w-full z-[9990] md:hidden no-print border-t border-[hsl(var(--border))] bg-background pb-[env(safe-area-inset-bottom)]"
      aria-label={t("navigation.main", "Main navigation")}
    >
      <div className="flex items-center h-16">
        {/* 1. Home */}
        <div className="flex justify-center items-center flex-1">
          <TabButton tab={homeTab} />
        </div>

        {/* 2. Library */}
        <div className="flex justify-center items-center flex-1">
          <TabButton tab={libraryTab} />
        </div>

        {/* 3. Center FAB — plain onClick button with isolated z-index stack for Android WebViews. */}
        <div ref={fabRef} className="relative z-[9999] flex justify-center items-center flex-1">
          {canCreate && fabOpen && (
            <div
              className="absolute bottom-[calc(100%+12px)] left-1/2 -translate-x-1/2 min-w-[220px] rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
              style={{ zIndex: 9999 }}
            >
              <button
                type="button"
                onClick={handleCreateDeck}
                className="mb-2 flex w-full items-center gap-2 rounded-xl bg-gray-100 p-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-gray-200 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700"
                style={{ border: "none", fontFamily: "DM Sans, sans-serif" }}
              >
                <CreditCard size={18} className="shrink-0" />
                <span>{t("buttons.cardDeck")}</span>
              </button>
              <button
                type="button"
                onClick={handleCreateFolder}
                className="flex w-full items-center gap-2 rounded-xl bg-gray-100 p-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-gray-200 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700"
                style={{ border: "none", fontFamily: "DM Sans, sans-serif" }}
              >
                <Folder size={18} className="shrink-0" />
                <span>{t("buttons.folder")}</span>
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              if (!canCreate) return;
              setFabOpen((open) => !open);
            }}
            className="flex h-12 w-12 items-center justify-center rounded-full shadow-lg"
            style={{
              zIndex: 9999,
              background: canCreate ? "rgb(147, 51, 234)" : "hsl(var(--muted))",
              color: canCreate ? "white" : "hsl(var(--muted-foreground))",
              border: "none",
              cursor: canCreate ? "pointer" : "default",
              position: "relative",
            }}
            aria-label={t("sidebar.newDeck")}
            aria-haspopup={canCreate ? "menu" : undefined}
            aria-expanded={canCreate ? fabOpen : undefined}
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>

        {/* 4. Achievements */}
        <div className="flex justify-center items-center flex-1">
          <TabButton tab={achievementsTab} />
        </div>

        {/* 5. Statistics */}
        <div className="flex justify-center items-center flex-1">
          <TabButton tab={statsTab} />
        </div>
      </div>
    </nav>
  );
}
